import { createContext, useContext } from "react";
import type { BillingContext, BuildingSubscription } from "./types";

export interface BillingStatusContextValue {
  orgId?: string;
  context: BillingContext | null;
  isReadOnly: boolean;
  detailsMissing: boolean;
  /** Card or SEPA is chosen but Stripe holds no method to charge. */
  paymentMethodMissing: boolean;
  isPilot: boolean;
  isLoading: boolean;
  isError: boolean;
  mutationStatusUnknown: boolean;
  /** Authoritative subscription for the building currently open in the router. */
  buildingSubscription?: BuildingSubscription;
  buildingProjectIds?: ReadonlySet<number>;
  refetch: () => Promise<unknown>;
}

/**
 * No billing state is asserted until a context has actually been read.
 *
 * Every flag defaults false rather than true: a consumer reading this default
 * is one mounted outside the provider, and a default that claimed "read only"
 * or "details missing" would withhold mutations across the whole app on the
 * strength of a value nobody fetched.
 */
const NOTHING_READ_YET = {
  isReadOnly: false,
  detailsMissing: false,
  paymentMethodMissing: false,
  isPilot: false,
  mutationStatusUnknown: false,
} as const;

export const BillingStatusContext = createContext<BillingStatusContextValue>({
  ...NOTHING_READ_YET,
  context: null,
  isLoading: true,
  isError: false,
  refetch: async () => undefined,
});

export function useBillingStatus() {
  return useContext(BillingStatusContext);
}

/** The server's rollout exemption is scoped to the selected organisation. */
export function hasLegacyBillingAccess(
  billing: Pick<BillingStatusContextValue, "orgId" | "context">
): boolean {
  return Boolean(
    billing.orgId &&
      billing.context?.org_id === billing.orgId &&
      billing.context.allows_legacy_building_creation === true
  );
}
